<script setup lang="ts">
import {
  GetSupplierDetail,
  GetSupplierFinancialSummary,
  parsePrimaryAddress,
} from "@/api/supplierApi";
import OpenHubLauncher from "@/components/record-hub/OpenHubLauncher.vue";
import PageEntryLoader from "@/components/shared/PageEntryLoader.vue";
import SupplierPaymentDialog from "@/components/supplier/SupplierPaymentDialog.vue";
import PaymentVoucherViewerDialog from "@/components/supplier/PaymentVoucherViewerDialog.vue";
import { usePaymentVoucher } from "@/composables/usePaymentVoucher";
import AddEditSupplierDialog from "@/components/supplier/AddEditSupplierDialog.vue";
import useToastify from "@/stores/toastify.store";
import type { FormType } from "@/type";
import type {
  AddEditSupplier,
  Supplier,
  SupplierFinancialSummary,
} from "@/type/supplier";
import { resolveFrappeFileUrl } from "@/services/frappe/files";

type SupplierTab = "overview" | "invoices" | "items" | "payments";

definePage({
  alias: "/warehouse/suppliers/:id",
});

const route = useRoute();
const router = useRouter();
const { t, locale } = useI18n();

/** Phone gate. Always `useMobileShell()`, never raw `useDisplay()` — it carries the kill switch. */
const { isMobileShell } = useMobileShell();
const toast = useToastify();
const {
  busy: voucherBusy, download: downloadVoucher, view: viewVoucher,
  previewOpen, previewBlob, previewFileName,
} = usePaymentVoucher();

const loading = ref(false);
const summaryLoading = ref(false);
const editDialogVisible = ref(false);
const paymentDialogVisible = ref(false);
const activeTab = ref<SupplierTab>("overview");
const supplier = ref<Supplier | null>(null);
const summary = ref<SupplierFinancialSummary | null>(null);
const formType = ref<FormType>("Edit");
const supplierForm = ref<AddEditSupplier>({
  supplier_name: "",
  supplier_type: "",
  email_id: "",
  mobile_no: "",
  address_line1: "",
  city: "",
  image: "",
});

const supplierImageSrc = computed(() =>
  supplier.value?.image ? resolveFrappeFileUrl(supplier.value.image) : null,
);

function routeParam(value: unknown) {
  return Array.isArray(value) ? String(value[0] ?? "") : String(value ?? "");
}

const supplierId = computed(() =>
  decodeURIComponent(routeParam((route.params as Record<string, unknown>).id)),
);

const currencyCode = computed(() => supplier.value?.default_currency || "IQD");

const numberFormatter = computed(() =>
  new Intl.NumberFormat(locale.value === "ar" ? "ar-IQ-u-nu-latn" : "en-US", {
    maximumFractionDigits: 2,
  }),
);

const moneyFormatter = computed(() => {
  try {
    return new Intl.NumberFormat(locale.value === "ar" ? "ar-IQ-u-nu-latn" : "en-US", {
      style: "currency",
      currency: currencyCode.value,
      maximumFractionDigits: currencyCode.value === "IQD" ? 0 : 2,
    });
  } catch {
    return numberFormatter.value;
  }
});

function formatNumber(value: number | null | undefined) {
  return numberFormatter.value.format(Number(value ?? 0));
}

function formatMoney(value: number | null | undefined) {
  return moneyFormatter.value.format(Number(value ?? 0));
}

function notSet(value: string | null | undefined) {
  return value || t("alkokh.global.notSet");
}

function encodePathPart(value: string | null | undefined) {
  return encodeURIComponent(String(value ?? ""));
}

function invoicePath(id: string) {
  return `/warehouse/purchase-invoices/${encodePathPart(id)}`;
}

function itemPath(id: string) {
  return `/warehouse/items/${encodePathPart(id)}`;
}

function paymentPath(id: string) {
  return `/accounting/payment-entries/${encodePathPart(id)}`;
}

const createPurchaseInvoiceRoute = computed(() => ({
  path: "/warehouse/purchase-invoices/create",
  query: {
    supplier: supplier.value?.name || supplierId.value,
    supplierName: supplier.value?.supplier_name || supplierId.value,
  },
}));

const supplierAddress = computed(() =>
  parsePrimaryAddress(supplier.value?.primary_address ?? null),
);

const statusMeta = computed(() =>
  supplier.value?.disabled
    ? { label: t("alkokh.enums.Inactive"), color: "error" }
    : { label: t("alkokh.enums.Active"), color: "success" },
);

const payableTone = computed(() => {
  if ((summary.value?.overdueOutstanding ?? 0) > 0)
    return "error";
  if ((summary.value?.totalOutstanding ?? 0) > 0)
    return "warning";

  return "success";
});

const heroChips = computed(() => [
  {
    label: notSet(supplier.value?.supplier_type),
    color: "primary",
  },
  {
    label: notSet(supplier.value?.supplier_group),
    color: "secondary",
  },
  {
    label: statusMeta.value.label,
    color: statusMeta.value.color,
  },
]);

const summaryCards = computed(() => [
  {
    label: t("alkokh.supplierHub.metrics.payable"),
    value: formatMoney(summary.value?.totalOutstanding),
    icon: "tabler-wallet",
    tone: payableTone.value,
  },
  {
    label: t("alkokh.supplierHub.metrics.overdue"),
    value: formatMoney(summary.value?.overdueOutstanding),
    icon: "tabler-alert-triangle",
    tone: (summary.value?.overdueOutstanding ?? 0) > 0 ? "error" : "success",
  },
  {
    label: t("alkokh.supplierHub.metrics.openInvoices"),
    value: formatNumber(summary.value?.openInvoiceCount),
    icon: "tabler-file-dollar",
    tone: "primary",
  },
  {
    label: t("alkokh.supplierHub.metrics.purchased"),
    value: formatMoney(summary.value?.totalPurchased),
    icon: "tabler-chart-bar",
    tone: "info",
  },
]);

const contactRows = computed(() => [
  {
    label: t("alkokh.setting.supplier.table.email"),
    value: supplier.value?.email_id || null,
    displayValue: notSet(supplier.value?.email_id),
    copyType: "email" as const,
    icon: "tabler-mail",
  },
  {
    label: t("alkokh.setting.supplier.table.mobileNo"),
    value: supplier.value?.mobile_no || null,
    displayValue: notSet(supplier.value?.mobile_no),
    copyType: "phone" as const,
    icon: "tabler-phone",
  },
  {
    label: t("alkokh.setting.supplier.table.address"),
    value: notSet(supplierAddress.value.address_line1),
    icon: "tabler-map-pin",
  },
  {
    label: t("alkokh.setting.supplier.table.city"),
    value: notSet(supplierAddress.value.city || supplier.value?.country || null),
    icon: "tabler-building",
  },
  {
    label: t("alkokh.supplierHub.fields.currency"),
    value: currencyCode.value,
    icon: "tabler-currency-dollar",
  },
  {
    label: t("alkokh.supplierHub.fields.paymentTerms"),
    value: notSet(supplier.value?.payment_terms),
    icon: "tabler-calendar-dollar",
  },
  {
    label: t("alkokh.setting.supplier.form.payableAccount"),
    value: notSet(supplier.value?.defaultPayableAccount),
    icon: "tabler-building-bank",
  },
]);

const signalRows = computed(() => [
  {
    label: t("alkokh.supplierHub.signals.nextDue"),
    value: notSet(summary.value?.nextDueDate),
    tone: (summary.value?.totalOutstanding ?? 0) > 0 ? "warning" : "success",
  },
  {
    label: t("alkokh.supplierHub.signals.lastInvoice"),
    value: summary.value?.lastInvoiceId
      ? `${summary.value.lastInvoiceId} · ${summary.value.lastInvoiceDate || "-"}`
      : t("alkokh.global.notSet"),
    tone: "primary",
  },
  {
    label: t("alkokh.supplierHub.signals.totalPaid"),
    value: formatMoney(summary.value?.totalPaid),
    tone: "success",
  },
  {
    label: t("alkokh.supplierHub.signals.paymentCount"),
    value: formatNumber(summary.value?.paymentCount),
    tone: "secondary",
  },
]);

const topReferencedItem = computed(() => summary.value?.referencedItems[0] ?? null);

const insightRows = computed(() => [
  {
    title: t("alkokh.supplierHub.insights.agingTitle"),
    caption: formatMoney(summary.value?.overdueOutstanding),
    icon: "tabler-clock-dollar",
    color: (summary.value?.overdueOutstanding ?? 0) > 0 ? "error" : "success",
  },
  {
    title: t("alkokh.supplierHub.insights.termsTitle"),
    caption: notSet(supplier.value?.payment_terms),
    icon: "tabler-calendar-stats",
    color: "primary",
  },
  {
    title: t("alkokh.supplierHub.insights.itemTitle"),
    caption: topReferencedItem.value
      ? `${topReferencedItem.value.itemName || topReferencedItem.value.itemCode} · ${formatMoney(topReferencedItem.value.amount)}`
      : t("alkokh.global.notSet"),
    icon: "tabler-packages",
    color: "warning",
  },
  {
    title: t("alkokh.supplierHub.insights.filesTitle"),
    caption: t("alkokh.supplierHub.insights.filesCaption"),
    icon: "tabler-paperclip",
    color: "secondary",
  },
]);

const tabs = computed(() => [
  { value: "overview", icon: "tabler-layout-dashboard", label: t("alkokh.supplierHub.tabs.overview") },
  { value: "invoices", icon: "tabler-file-invoice", label: t("alkokh.supplierHub.tabs.invoices") },
  { value: "items", icon: "tabler-box-seam", label: t("alkokh.supplierHub.tabs.items") },
  { value: "payments", icon: "tabler-cash-banknote", label: t("alkokh.supplierHub.tabs.payments") },
]);

function resolveStatusColor(status: string | null | undefined) {
  if (status === "Submitted" || status === "Paid")
    return "success";
  if (status === "Cancelled")
    return "error";
  if (status === "Partially Paid")
    return "warning";

  return "secondary";
}

function syncSupplierForm(nextSupplier: Supplier) {
  const address = parsePrimaryAddress(nextSupplier.primary_address);

  supplierForm.value = {
    name: nextSupplier.name,
    supplier_name: nextSupplier.supplier_name,
    supplier_type: nextSupplier.supplier_type || "",
    email_id: nextSupplier.email_id || "",
    mobile_no: nextSupplier.mobile_no || "",
    address_line1: address.address_line1,
    city: address.city || nextSupplier.country || "",
    image: nextSupplier.image || "",
    accounts: nextSupplier.accounts ?? [],
    defaultPayableCompany: nextSupplier.defaultPayableCompany ?? null,
    defaultPayableAccount: nextSupplier.defaultPayableAccount ?? null,
    advanceAccount: nextSupplier.advanceAccount ?? null,
  };
}

async function fetchSupplier() {
  supplier.value = await GetSupplierDetail(loading, supplierId.value);

  if (supplier.value) {
    syncSupplierForm(supplier.value);
    return;
  }

  toast.error({ text: t("alkokh.supplierHub.messages.loadFailed") });
}

async function fetchSummary() {
  summary.value = await GetSupplierFinancialSummary(summaryLoading, supplierId.value);
}

async function loadPage() {
  if (!supplierId.value)
    return;

  await Promise.all([
    fetchSupplier(),
    fetchSummary(),
  ]);
}

async function handleAfterUpdate() {
  await fetchSupplier();
}

async function handlePaymentCreated() {
  await fetchSummary();
  activeTab.value = "payments";
}

onMounted(loadPage);
</script>

<template>
  <div class="supplier-hub-page d-flex flex-column gap-6">
    <div class="supplier-hub-page__toolbar">
      <VBtn
        variant="tonal"
        color="secondary"
        prepend-icon="tabler-arrow-left"
        @click="router.push('/warehouse/suppliers')"
      >
        {{ t("alkokh.global.backToList") }}
      </VBtn>

      <div class="supplier-hub-page__toolbar-actions">
        <VBtn
          color="secondary"
          variant="outlined"
          prepend-icon="tabler-refresh"
          :loading="loading || summaryLoading"
          @click="loadPage"
        >
          {{ t("alkokh.global.refresh") }}
        </VBtn>

        <VBtn
          v-if="supplier"
          color="primary"
          prepend-icon="tabler-edit"
          @click="editDialogVisible = true"
        >
          {{ t("alkokh.global.edit") }}
        </VBtn>
      </div>
    </div>

    <PageEntryLoader
      v-if="loading"
      :title="t('alkokh.supplierHub.detailTitle')"
      :subtitle="supplierId"
    />

    <VAlert
      v-else-if="!supplier"
      color="warning"
      variant="tonal"
    >
      {{ t("alkokh.supplierHub.notFound") }}
    </VAlert>

    <template v-else>
      <section class="supplier-hub-page__hero">
        <div class="supplier-hub-page__identity">
          <div class="supplier-hub-page__avatar">
            <VImg
              v-if="supplierImageSrc"
              :src="supplierImageSrc"
              :alt="supplier.supplier_name"
              cover
            />
            <VAvatar
              v-else
              size="76"
              color="primary"
              variant="tonal"
            >
              <VIcon
                icon="tabler-truck-delivery"
                size="36"
              />
            </VAvatar>
          </div>

          <div class="min-w-0">
            <div class="d-flex flex-wrap gap-2 mb-3">
              <VChip
                v-for="chip in heroChips"
                :key="`${chip.label}-${chip.color}`"
                :color="chip.color"
                variant="tonal"
                size="small"
              >
                {{ chip.label }}
              </VChip>
            </div>

            <div class="text-h3 font-weight-bold text-wrap">
              {{ supplier.supplier_name }}
            </div>
            <div class="text-body-1 text-medium-emphasis mt-1">
              {{ supplier.name }}
            </div>
          </div>
        </div>

        <div class="supplier-hub-page__payable" :class="`supplier-hub-page__payable--${payableTone}`">
          <span>{{ t("alkokh.supplierHub.metrics.payable") }}</span>
          <strong>{{ formatMoney(summary?.totalOutstanding) }}</strong>
          <small>{{ t("alkokh.supplierHub.payableCaption", { count: summary?.openInvoiceCount || 0 }) }}</small>
        </div>
      </section>

      <div class="supplier-hub-page__metrics">
        <VCard
          v-for="card in summaryCards"
          :key="card.label"
          variant="outlined"
          class="supplier-hub-page__metric-card"
        >
          <VCardText class="pa-4">
            <div class="supplier-hub-page__metric-head">
              <span>{{ card.label }}</span>
              <VIcon
                :icon="card.icon"
                :color="card.tone"
                size="22"
              />
            </div>
            <div
              class="text-h5 font-weight-bold mt-3"
              :class="`text-${card.tone}`"
            >
              {{ card.value }}
            </div>
          </VCardText>
        </VCard>
      </div>

      <div class="supplier-hub-page__layout">
        <main class="supplier-hub-page__main">
          <div class="supplier-hub-page__tabs-wrap">
            <VTabs
              v-model="activeTab"
              class="supplier-hub-page__tabs"
              density="comfortable"
              :height="isMobileShell ? '48px' : '70px'"
            >
              <VTab
                v-for="tab in tabs"
                :key="tab.value"
                :value="tab.value"
                
              >
                <VIcon
                  :icon="tab.icon"
                  size="18"
                  start
                />
                {{ tab.label }}
              </VTab>
            </VTabs>
          </div>

          <VWindow v-model="activeTab" class="supplier-hub-page__window">
            <VWindowItem value="overview">
              <div class="supplier-hub-page__overview-grid">
                <section class="supplier-hub-page__panel">
                  <div class="supplier-hub-page__panel-head">
                    <div>
                      <h2 class="text-h6 font-weight-bold">
                        {{ t("alkokh.supplierHub.sections.financialSignals") }}
                      </h2>
                      <div class="text-body-2 text-medium-emphasis mt-1">
                        {{ t("alkokh.supplierHub.sections.financialSignalsSubtitle") }}
                      </div>
                    </div>
                  </div>

                  <div class="supplier-hub-page__signal-grid mt-5">
                    <div
                      v-for="signal in signalRows"
                      :key="signal.label"
                      class="supplier-hub-page__signal"
                    >
                      <span>{{ signal.label }}</span>
                      <strong :class="`text-${signal.tone}`">{{ signal.value }}</strong>
                    </div>
                  </div>
                </section>

                <section class="supplier-hub-page__panel">
                  <div class="supplier-hub-page__panel-head">
                    <div>
                      <h2 class="text-h6 font-weight-bold">
                        {{ t("alkokh.supplierHub.sections.recentInvoices") }}
                      </h2>
                      <div class="text-body-2 text-medium-emphasis mt-1">
                        {{ t("alkokh.supplierHub.sections.recentInvoicesSubtitle") }}
                      </div>
                    </div>
                  </div>

                  <div
                    v-if="summary?.invoices.length"
                    class="supplier-hub-page__mini-list mt-5"
                  >
                    <RouterLink
                      v-for="invoice in summary.invoices.slice(0, 5)"
                      :key="invoice.id"
                      :to="invoicePath(invoice.id)"
                      class="supplier-hub-page__mini-row"
                    >
                      <div>
                        <strong>{{ invoice.id }}</strong>
                        <span>{{ invoice.postingDate || "-" }}</span>
                      </div>
                      <div class="text-end">
                        <strong>{{ formatMoney(invoice.outstandingAmount) }}</strong>
                        <span>{{ invoice.paymentStatus || invoice.status }}</span>
                      </div>
                    </RouterLink>
                  </div>

                  <VAlert
                    v-else
                    class="mt-5"
                    color="secondary"
                    variant="tonal"
                  >
                    {{ t("alkokh.supplierHub.empty.invoices") }}
                  </VAlert>
                </section>

                <section class="supplier-hub-page__panel supplier-hub-page__span-2">
                  <div class="supplier-hub-page__panel-head">
                    <div>
                      <h2 class="text-h6 font-weight-bold">
                        {{ t("alkokh.supplierHub.sections.referencedItems") }}
                      </h2>
                      <div class="text-body-2 text-medium-emphasis mt-1">
                        {{ t("alkokh.supplierHub.sections.referencedItemsSubtitle") }}
                      </div>
                    </div>
                  </div>

                  <div
                    v-if="summary?.referencedItems.length"
                    class="supplier-hub-page__item-strip mt-5"
                  >
                    <RouterLink
                      v-for="item in summary.referencedItems.slice(0, 4)"
                      :key="item.itemCode"
                      :to="itemPath(item.itemCode)"
                      class="supplier-hub-page__item-pill"
                    >
                      <VIcon
                        icon="tabler-box"
                        color="primary"
                        size="20"
                      />
                      <div class="min-w-0">
                        <strong>{{ item.itemName || item.itemCode }}</strong>
                        <span>{{ t("alkokh.supplierHub.itemCaption", { count: item.invoiceCount, amount: formatMoney(item.amount) }) }}</span>
                      </div>
                    </RouterLink>
                  </div>

                  <VAlert
                    v-else
                    class="mt-5"
                    color="secondary"
                    variant="tonal"
                  >
                    {{ t("alkokh.supplierHub.empty.items") }}
                  </VAlert>
                </section>
              </div>
            </VWindowItem>

            <VWindowItem value="invoices">
              <section class="supplier-hub-page__panel">
                <div class="supplier-hub-page__panel-head">
                  <div>
                    <h2 class="text-h6 font-weight-bold">
                      {{ t("alkokh.supplierHub.sections.purchaseInvoices") }}
                    </h2>
                    <div class="text-body-2 text-medium-emphasis mt-1">
                      {{ t("alkokh.supplierHub.sections.purchaseInvoicesSubtitle") }}
                    </div>
                  </div>

                  <VBtn
                    color="primary"
                    variant="tonal"
                    prepend-icon="tabler-plus"
                    :to="createPurchaseInvoiceRoute"
                  >
                    {{ t("alkokh.purchaseInvoice.createAction") }}
                  </VBtn>
                </div>

                <VTable class="supplier-hub-page__table mt-5">
                  <thead>
                    <tr>
                      <th>{{ t("alkokh.purchaseInvoice.columns.invoice") }}</th>
                      <th>{{ t("alkokh.purchaseInvoice.columns.postingDate") }}</th>
                      <th>{{ t("alkokh.purchaseInvoice.columns.dueDate") }}</th>
                      <th>{{ t("alkokh.global.status") }}</th>
                      <th class="text-end">{{ t("alkokh.purchaseInvoice.columns.total") }}</th>
                      <th class="text-end">{{ t("alkokh.purchaseInvoice.columns.outstanding") }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="invoice in summary?.invoices || []"
                      :key="invoice.id"
                    >
                      <td>
                        <RouterLink
                          :to="invoicePath(invoice.id)"
                          class="font-weight-medium"
                        >
                          {{ invoice.id }}
                        </RouterLink>
                        <div class="text-body-2 text-medium-emphasis">
                          {{ invoice.billNo || "-" }}
                        </div>
                      </td>
                      <td>{{ invoice.postingDate || "-" }}</td>
                      <td>{{ invoice.dueDate || "-" }}</td>
                      <td>
                        <VChip
                          size="small"
                          variant="tonal"
                          :color="resolveStatusColor(invoice.paymentStatus || invoice.status)"
                        >
                          {{ invoice.paymentStatus || invoice.status }}
                        </VChip>
                      </td>
                      <td class="text-end">{{ formatMoney(invoice.total) }}</td>
                      <td class="text-end font-weight-medium">{{ formatMoney(invoice.outstandingAmount) }}</td>
                    </tr>
                    <tr v-if="!summary?.invoices.length">
                      <td colspan="6">
                        {{ t("alkokh.supplierHub.empty.invoices") }}
                      </td>
                    </tr>
                  </tbody>
                </VTable>
              </section>
            </VWindowItem>

            <VWindowItem value="items">
              <section class="supplier-hub-page__panel">
                <div class="supplier-hub-page__panel-head">
                  <div>
                    <h2 class="text-h6 font-weight-bold">
                      {{ t("alkokh.supplierHub.sections.referencedItems") }}
                    </h2>
                    <div class="text-body-2 text-medium-emphasis mt-1">
                      {{ t("alkokh.supplierHub.sections.referencedItemsSubtitle") }}
                    </div>
                  </div>
                </div>

                <VTable class="supplier-hub-page__table mt-5">
                  <thead>
                    <tr>
                      <th>{{ t("alkokh.purchaseInvoice.columns.item") }}</th>
                      <th>{{ t("alkokh.supplierHub.columns.invoiceCount") }}</th>
                      <th class="text-end">{{ t("alkokh.purchaseInvoice.columns.qty") }}</th>
                      <th class="text-end">{{ t("alkokh.purchaseInvoice.columns.amount") }}</th>
                      <th>{{ t("alkokh.supplierHub.voucher.column") }}</th>
                      <th>{{ t("alkokh.supplierHub.columns.lastPurchase") }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="item in summary?.referencedItems || []"
                      :key="item.itemCode"
                    >
                      <td>
                        <RouterLink
                          :to="itemPath(item.itemCode)"
                          class="font-weight-medium"
                        >
                          {{ item.itemName || item.itemCode }}
                        </RouterLink>
                        <div class="text-body-2 text-medium-emphasis">
                          {{ item.itemCode }}
                        </div>
                      </td>
                      <td>{{ item.invoiceCount }}</td>
                      <td class="text-end">{{ formatNumber(item.qty) }}</td>
                      <td class="text-end font-weight-medium">{{ formatMoney(item.amount) }}</td>
                      <td>
                        <RouterLink
                          v-if="item.lastInvoiceId"
                          :to="invoicePath(item.lastInvoiceId)"
                        >
                          {{ item.lastPurchaseDate || item.lastInvoiceId }}
                        </RouterLink>
                        <span v-else>-</span>
                      </td>
                    </tr>
                    <tr v-if="!summary?.referencedItems.length">
                      <td colspan="5">
                        {{ t("alkokh.supplierHub.empty.items") }}
                      </td>
                    </tr>
                  </tbody>
                </VTable>
              </section>
            </VWindowItem>

            <VWindowItem value="payments">
              <section class="supplier-hub-page__panel">
                <div class="supplier-hub-page__panel-head">
                  <div>
                    <h2 class="text-h6 font-weight-bold">
                      {{ t("alkokh.supplierHub.sections.payments") }}
                    </h2>
                    <div class="text-body-2 text-medium-emphasis mt-1">
                      {{ t("alkokh.supplierHub.sections.paymentsSubtitle") }}
                    </div>
                  </div>

                  <VBtn
                    color="primary"
                    variant="tonal"
                    prepend-icon="tabler-cash-banknote"
                    @click="paymentDialogVisible = true"
                  >
                    {{ t("alkokh.supplierHub.actions.createPayment") }}
                  </VBtn>
                </div>

                <VTable class="supplier-hub-page__table mt-5">
                  <thead>
                    <tr>
                      <th>{{ t("alkokh.supplierHub.columns.payment") }}</th>
                      <th>{{ t("alkokh.purchaseInvoice.columns.postingDate") }}</th>
                      <th>{{ t("alkokh.supplierHub.columns.mode") }}</th>
                      <th>{{ t("alkokh.global.status") }}</th>
                      <th class="text-end">{{ t("alkokh.purchaseInvoice.columns.amount") }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="payment in summary?.payments || []"
                      :key="payment.id"
                    >
                      <td>
                        <RouterLink
                          :to="paymentPath(payment.id)"
                          class="font-weight-medium"
                        >
                          {{ payment.id }}
                        </RouterLink>
                        <div class="text-body-2 text-medium-emphasis">
                          {{ payment.referenceNo || "-" }}
                        </div>
                      </td>
                      <td>{{ payment.postingDate || "-" }}</td>
                      <td>{{ payment.modeOfPayment || "-" }}</td>
                      <td>
                        <VChip
                          size="small"
                          variant="tonal"
                          :color="resolveStatusColor(payment.status)"
                        >
                          {{ payment.status }}
                        </VChip>
                      </td>
                      <td class="text-end font-weight-medium">{{ formatMoney(payment.amount) }}</td>
                      <td>
                        <div class="d-flex align-center gap-1 flex-wrap">
                          <VBtn
                            v-if="payment.status === 'Submitted' && payment.paymentType === 'Pay'"
                            size="small"
                            variant="text"
                            prepend-icon="tabler-eye"
                            :loading="voucherBusy"
                            @click="viewVoucher(payment.id)"
                          >
                            {{ t("alkokh.supplierHub.voucher.view") }}
                          </VBtn>
                          <VBtn
                            v-if="payment.status === 'Submitted' && payment.paymentType === 'Pay'"
                            size="small"
                            variant="text"
                            prepend-icon="tabler-file-type-pdf"
                            :loading="voucherBusy"
                            @click="downloadVoucher(payment.id)"
                          >
                            {{ t("alkokh.supplierHub.voucher.download") }}
                          </VBtn>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="!summary?.payments.length">
                      <td colspan="6">
                        {{ t("alkokh.supplierHub.empty.payments") }}
                      </td>
                    </tr>
                  </tbody>
                </VTable>
              </section>
            </VWindowItem>
          </VWindow>
        </main>

        <aside class="supplier-hub-page__rail">
          <section class="supplier-hub-page__panel">
            <div class="supplier-hub-page__panel-head">
              <div>
                <h2 class="text-h6 font-weight-bold">
                  {{ t("alkokh.supplierHub.sections.profile") }}
                </h2>
                <div class="text-body-2 text-medium-emphasis mt-1">
                  {{ t("alkokh.supplierHub.sections.profileSubtitle") }}
                </div>
              </div>
            </div>

            <div class="supplier-hub-page__contact-list mt-5">
              <div
                v-for="row in contactRows"
                :key="row.label"
                class="supplier-hub-page__contact-row"
              >
                <VIcon
                  :icon="row.icon"
                  color="secondary"
                  size="18"
                />
                <div>
                  <span>{{ row.label }}</span>
                  <strong>
                    <CopyableText
                      v-if="row.copyType"
                      :value="row.value"
                      :type="row.copyType"
                      :empty-text="row.displayValue"
                    />
                    <template v-else>
                      {{ row.value }}
                    </template>
                  </strong>
                </div>
              </div>
            </div>
          </section>

          <section class="supplier-hub-page__panel">
            <div class="supplier-hub-page__panel-head">
              <div>
                <h2 class="text-h6 font-weight-bold">
                  {{ t("alkokh.supplierHub.sections.decisionCues") }}
                </h2>
                <div class="text-body-2 text-medium-emphasis mt-1">
                  {{ t("alkokh.supplierHub.sections.decisionCuesSubtitle") }}
                </div>
              </div>
            </div>

            <div class="supplier-hub-page__insight-list mt-5">
              <div
                v-for="insight in insightRows"
                :key="insight.title"
                class="supplier-hub-page__insight-row"
              >
                <VAvatar
                  :color="insight.color"
                  variant="tonal"
                  size="36"
                >
                  <VIcon
                    :icon="insight.icon"
                    size="19"
                  />
                </VAvatar>
                <div class="min-w-0">
                  <strong>{{ insight.title }}</strong>
                  <span>{{ insight.caption }}</span>
                </div>
              </div>
            </div>
          </section>
        </aside>
      </div>

      <AddEditSupplierDialog
        v-model="editDialogVisible"
        v-model:supplier="supplierForm"
        :form-type="formType"
        @update="handleAfterUpdate"
      />

      <SupplierPaymentDialog
        v-model="paymentDialogVisible"
        :supplier="supplier.name"
        :supplier-name="supplier.supplier_name"
        :outstanding-amount="summary?.totalOutstanding || 0"
        @created="handlePaymentCreated"
      />

      <PaymentVoucherViewerDialog
        v-model="previewOpen"
        :blob="previewBlob"
        :file-name="previewFileName"
      />

      <OpenHubLauncher
        doctype="Supplier"
        :docname="supplier.name"
        :title="supplier.supplier_name"
        :subtitle="supplier.name"
        :status-label="statusMeta.label"
      />
    </template>
  </div>
</template>

<style scoped>
.supplier-hub-page {
  background: transparent;
}

.supplier-hub-page__toolbar,
.supplier-hub-page__toolbar-actions,
.supplier-hub-page__panel-head,
.supplier-hub-page__metric-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
}

.supplier-hub-page__hero {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  gap: 1rem;
  padding: 1.25rem;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  background: rgb(var(--v-theme-surface));
}

.supplier-hub-page__identity {
  display: flex;
  align-items: center;
  gap: 1.25rem;
  min-inline-size: 0;
}

.supplier-hub-page__avatar {
  display: grid;
  overflow: hidden;
  place-items: center;
  inline-size: 108px;
  block-size: 108px;
  border-radius: 8px;
  background: rgba(var(--v-theme-primary), 0.08);
  flex-shrink: 0;
}

.supplier-hub-page__payable {
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-inline-size: 240px;
  padding: 1rem;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-inline-start-width: 4px;
  border-radius: 8px;
  background: rgba(var(--v-theme-surface), 0.86);
}

.supplier-hub-page__payable span,
.supplier-hub-page__payable small,
.supplier-hub-page__signal span,
.supplier-hub-page__contact-row span,
.supplier-hub-page__insight-row span,
.supplier-hub-page__mini-row span,
.supplier-hub-page__item-pill span {
  color: rgba(var(--v-theme-on-surface), 0.68);
  font-size: 0.8125rem;
}

.supplier-hub-page__payable strong {
  margin-block: 0.35rem;
  font-size: 1.6rem;
  line-height: 1.2;
}

.supplier-hub-page__payable--success {
  border-inline-start-color: rgb(var(--v-theme-success));
}

.supplier-hub-page__payable--warning {
  border-inline-start-color: rgb(var(--v-theme-warning));
}

.supplier-hub-page__payable--error {
  border-inline-start-color: rgb(var(--v-theme-error));
}

.supplier-hub-page__metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
}

.supplier-hub-page__metric-card,
.supplier-hub-page__panel {
  border-radius: 8px;
  background: rgb(var(--v-theme-surface));
}

.supplier-hub-page__layout {
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(300px, 1fr);
  gap: 1rem;
}

.supplier-hub-page__main,
.supplier-hub-page__rail {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  min-inline-size: 0;
}

.supplier-hub-page__tabs-wrap {
  
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  background: rgb(var(--v-theme-surface));
}

.supplier-hub-page__tabs :deep(.v-slide-group__content) {
  min-block-size: 70px;
}

.supplier-hub-page__tabs :deep(.v-tab) {
  min-block-size: 70px;
  text-transform: none;
  max-width: none;
  flex: 1;
}

.supplier-hub-page__tabs :deep(.v-tab__content) {
  overflow: visible;
  text-overflow: unset;
  white-space: nowrap;

}

.supplier-hub-page__tabs :deep(.v-tab__content span) {
  text-overflow: unset;
  overflow: visible;
}

.supplier-hub-page__window {
  overflow: visible;
}

.supplier-hub-page__overview-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.supplier-hub-page__span-2 {
  grid-column: 1 / -1;
}

.supplier-hub-page__panel {
  padding: 1.25rem;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
}

.supplier-hub-page__signal-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 0.75rem;
}

.supplier-hub-page__signal,
.supplier-hub-page__contact-row,
.supplier-hub-page__insight-row,
.supplier-hub-page__mini-row,
.supplier-hub-page__item-pill {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.85rem;
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  background: rgba(var(--v-theme-surface), 0.72);
}

.supplier-hub-page__signal {
  align-items: flex-start;
  flex-direction: column;
}

.supplier-hub-page__signal strong {
  font-size: 1rem;
}

.supplier-hub-page__mini-list,
.supplier-hub-page__contact-list,
.supplier-hub-page__insight-list {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.supplier-hub-page__mini-row {
  justify-content: space-between;
  color: inherit;
  text-decoration: none;
}

.supplier-hub-page__mini-row > div,
.supplier-hub-page__contact-row > div,
.supplier-hub-page__insight-row > div {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  min-inline-size: 0;
}

.supplier-hub-page__item-strip {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: 0.75rem;
}

.supplier-hub-page__item-pill {
  color: inherit;
  text-decoration: none;
}

.supplier-hub-page__item-pill strong,
.supplier-hub-page__insight-row strong,
.supplier-hub-page__contact-row strong,
.supplier-hub-page__mini-row strong {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.supplier-hub-page__table {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
}

.supplier-hub-page__table :deep(th) {
  color: rgba(var(--v-theme-on-surface), 0.72);
  font-size: 0.75rem;
  text-transform: uppercase;
}

@media (max-width: 1199px) {
  .supplier-hub-page__metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .supplier-hub-page__layout {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 767px) {
  .supplier-hub-page__hero,
  .supplier-hub-page__identity,
  .supplier-hub-page__toolbar {
    align-items: flex-start;
    flex-direction: column;
  }

  .supplier-hub-page__payable {
    inline-size: 100%;
  }

  .supplier-hub-page__overview-grid,
  .supplier-hub-page__signal-grid {
    grid-template-columns: 1fr;
  }

  /*
   * ⚠️ Metrics stay TWO-UP. Stacking four stat tiles into one column pushes the tabs far below the
   * fold, so the record's actual content is unreachable on arrival.
   */
  .supplier-hub-page__metrics {
    gap: 0.5rem;
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  /* 108px of avatar is a quarter of a 390px screen — the name and status matter more. */
  .supplier-hub-page__avatar {
    block-size: 72px;
    inline-size: 72px;
  }
}
</style>
